<script setup lang="ts">
import { toRef, ref, computed } from 'vue'
import { PlusOutlined } from '@ant-design/icons-vue'
import { stringParam, useQueryParam } from 'vue-use-query-param'

import { useLinkedFarmInvitationsWithLinkedFarms } from '@/composables/linkedFarmInvitations'
import {
  useSearchValueWithSearchParams,
  useActiveTabWithSearchParams,
} from '@/composables/searchParams'
import { useMarkable } from '@/composables/markable'
import { useResourcePermissions } from '@/composables/permissions'
import { useOrganisation } from '@/composables/organisations'

import CreateInviteModal from '@/components/Modals/VisionOrg/CreateInvite.vue'
import EditInviteModal from '@/components/Modals/VisionOrg/EditInvite.vue'
import FilterSection from '@/components/ContentSections/FilterSection.vue'
import PageHeading from '@/components/ContentSections/PageHeading.vue'
import InviteCard from '@/components/Cards/VisionOrg/Invite.vue'
import InvitePendingCard from '@/components/Cards/VisionOrg/InvitePending.vue'
import LinkedBusinessPanel from '@/components/Panels/LinkedBusinessPanel.vue'
import Virtualizer from '@/components/Virtualizer.vue'

const { activeTab, onTabChange } = useActiveTabWithSearchParams()
const { searchVal, clearSearchValue } = useSearchValueWithSearchParams()
const { currentOrg } = useOrganisation()
useMarkable(searchVal)

const selectedLinkedBusinessId = useQueryParam('selectedLinkedBusinessId', stringParam())

const {
  isFetchingLinkedFarms,
  isFetchingInvitations,
  usedInvitations,
  unusedInvitations,
  pendingInvitations,
  pendingAndLinkedFarmsByOrgMap,
  linkedFarmsWithInvitation,
} = useLinkedFarmInvitationsWithLinkedFarms()
const createInvite = ref(false)

const selectedLinkedBusiness = computed(() => {
  if (!selectedLinkedBusinessId.value) return undefined
  return (linkedFarmsWithInvitation.value ?? []).find(
    (p) => p.property.id === selectedLinkedBusinessId.value,
  )
})

const filteredUsedInvitations = computed(() => {
  const searchValueLC = searchVal.value?.toLowerCase()
  if (!searchValueLC) return usedInvitations.value

  return (usedInvitations.value ?? []).filter((c) => {
    if ('firstName' in c.recipientDetails) {
      const name = `${c.recipientDetails.firstName} ${c.recipientDetails.lastName}`
      if (name.toLowerCase().includes(searchValueLC)) return true
    }
    if (c.primaryOrganisation) {
      if (c.primaryOrganisation.name.toLowerCase().includes(searchValueLC)) return true

      const businesses = pendingAndLinkedFarmsByOrgMap.value.get(c.primaryOrganisation.id)
      if (businesses?.some((b) => b.property.propertyName.toLowerCase().includes(searchValueLC)))
        return true
    }
    if (c.invite.clientReferenceId) {
      if (c.invite.clientReferenceId.toLowerCase().includes(searchValueLC)) return true
    }
    return c.recipientDetails.email.toLowerCase().includes(searchValueLC)
  })
})

const filteredPendingInvitations = computed(() => {
  const searchValueLC = searchVal.value?.toLowerCase()
  if (!searchValueLC) return pendingInvitations.value

  return (pendingInvitations.value ?? []).filter((c) => {
    if ('firstName' in c.recipientDetails) {
      const name = `${c.recipientDetails.firstName} ${c.recipientDetails.lastName}`
      if (name.toLowerCase().includes(searchValueLC)) return true
    }
    if (c.primaryOrganisation) {
      if (c.primaryOrganisation.name.toLowerCase().includes(searchValueLC)) return true

      const businesses = pendingAndLinkedFarmsByOrgMap.value.get(c.primaryOrganisation.id)
      if (businesses?.some((b) => b.property.propertyName.toLowerCase().includes(searchValueLC)))
        return true
    }
    if (c.invite.clientReferenceId) {
      if (c.invite.clientReferenceId.toLowerCase().includes(searchValueLC)) return true
    }
    return c.recipientDetails.email.toLowerCase().includes(searchValueLC)
  })
})

const { allowedToCreateInvitation } = useResourcePermissions(toRef(() => currentOrg.value?.id))
const editInviteId = ref<null | string>(null)
</script>

<template>
  <PageHeading title="Invitations">
    <template #right>
      <a-button type="primary" :disabled="!allowedToCreateInvitation" @click="createInvite = true">
        <PlusOutlined />
        Invite Organisation to Share
      </a-button>
    </template>
  </PageHeading>

  <LinkedBusinessPanel
    v-if="selectedLinkedBusiness"
    :linked-business="selectedLinkedBusiness"
    :visible="typeof selectedLinkedBusinessId === 'string'"
    :set-invisible="() => (selectedLinkedBusinessId = undefined)"
  />
  <EditInviteModal
    v-if="editInviteId"
    :invite-id="editInviteId"
    :show-edit-invite-modal="editInviteId !== null"
    @close-modal="editInviteId = null"
  />

  <CreateInviteModal
    v-if="createInvite"
    :visible="createInvite"
    :on-cancel="() => (createInvite = false)"
    :on-complete="() => (createInvite = false)"
  />

  <FilterSection
    placeholder="Search by Organisation, Key Contact or Business Name"
    :hide-filter="true"
    :filter-count="searchVal ? 1 : 0"
    :on-clear="
      () => {
        clearSearchValue()
      }
    "
  />

  <a-skeleton
    v-if="isFetchingInvitations || isFetchingLinkedFarms"
    active
    :paragraph="{ rows: 8 }"
  />

  <a-tabs v-else :active-key="activeTab" class="min-h-0" @change="onTabChange">
    <a-tab-pane key="active">
      <template #tab>
        <a-flex align="center">
          Active Invitations
          <a-badge
            class="ml-2"
            color="#EDEDED"
            :count="filteredUsedInvitations?.length"
            :overflow-count="999"
          />
        </a-flex>
      </template>
      <Virtualizer
        v-if="filteredUsedInvitations !== undefined && filteredUsedInvitations.length > 0"
        :items="filteredUsedInvitations"
        :estimate-size="() => 192"
      >
        <template #component="{ item }">
          <InviteCard
            :key="item.invite.id"
            :invitation="item"
            :set-edit-invite-id="(inviteId) => (editInviteId = inviteId)"
            class="mb-6"
          />
        </template>
      </Virtualizer>
    </a-tab-pane>

    <a-tab-pane key="pending">
      <template #tab>
        <a-flex align="center">
          Pending Requests
          <a-badge
            class="ml-2"
            :class="{
              redBadge: filteredPendingInvitations && filteredPendingInvitations?.length > 0,
              pendingZeroBadge: filteredPendingInvitations?.length === 0,
            }"
            :color="filteredPendingInvitations?.length === 0 ? '#EDEDED' : undefined"
            :count="filteredPendingInvitations?.length ?? 0"
            :overflow-count="999"
          />
        </a-flex>
      </template>
      <Virtualizer
        v-if="filteredPendingInvitations !== undefined && filteredPendingInvitations.length > 0"
        :items="filteredPendingInvitations"
        :estimate-size="() => 192"
      >
        <template #component="{ item }">
          <InvitePendingCard
            :key="item.invite.id"
            :invitation="item"
            :set-edit-invite-id="(inviteId) => (editInviteId = inviteId)"
            class="mb-6"
          />
        </template>
      </Virtualizer>
    </a-tab-pane>

    <a-tab-pane key="awaiting">
      <template #tab>
        <a-flex align="center">
          Invitations Awaiting Recipient Action
          <a-badge class="ml-2" color="#EDEDED" :count="unusedInvitations?.length" />
        </a-flex>
      </template>
      <a-empty
        v-if="unusedInvitations?.length === 0"
        description="No Invitations awaiting recipient action"
      />
      <Virtualizer
        v-if="unusedInvitations !== undefined && unusedInvitations.length > 0"
        :items="unusedInvitations"
        :estimate-size="() => 192"
      >
        <template #component="{ item }">
          <InviteCard
            :key="item.invite.id"
            :invitation="item"
            :set-edit-invite-id="(inviteId) => (editInviteId = inviteId)"
            class="mb-6"
          />
        </template>
      </Virtualizer>
    </a-tab-pane>
  </a-tabs>
</template>

<style scoped>
:deep(.ant-tabs-content-holder) {
  /* required to get virtual list displaying with correct height in a-tabs */
  display: flex;
}
</style>
